/**
 * Durasan bundle / upsell — manually selected products for PDP, cart, and source-specific popup.
 * Discount itself is a Shopify automatic BXGY; the cart price is never supplied by this file. The PDP shows a conditional Liquid price preview.
 */
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const STORAGE_KEY_FALLBACK = 'durasan:bundle-upsell:v1';

function config() {
  const node = document.getElementById('du-bundle-config');
  if (!node?.textContent) return {};
  try {
    return JSON.parse(node.textContent);
  } catch {
    return {};
  }
}

function sessionKey() {
  return config().storageKey || STORAGE_KEY_FALLBACK;
}

function seen() {
  try {
    return sessionStorage.getItem(sessionKey()) === '1';
  } catch {
    return true;
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(sessionKey(), '1');
  } catch {
    /* private mode */
  }
}

function cartItems(detail) {
  return Array.isArray(detail?.items) ? detail.items : [];
}

function numericId(value) {
  return String(value || '').split('/').pop();
}

function isSourceAddition(event, cfg) {
  if (!cfg.sourceProductId || !Array.isArray(cfg.sourceVariantIds)) return false;
  const variants = new Set(cfg.sourceVariantIds.map(numericId));
  return Array.isArray(event.lines) && event.lines.some((line) => variants.has(numericId(line.merchandiseId)));
}

function cartAddUrl() {
  return (
    globalThis.Theme?.routes?.cart_add_url ||
    `${window.Shopify?.routes?.root || '/'}cart/add.js`
  );
}

function cartJsonUrl() {
  const cartUrl = globalThis.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
  return `${cartUrl}.js`;
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

let addingBundle = false;
let openingModal = false;
let uncertainAdd = false;
let lastKnownCart = null;
let cartRevision = 0;
const pendingCartUpdates = new Set();

function isFullPair(data) {
  return data.duBundleContext === 'pdp' && data.duBundleFullPair === 'true';
}

export function bundleOfferState(cart, data, now = Date.now()) {
  const percent = Number(data.duBundleDiscountPercent);
  const start = data.duBundleStartsAt ? Number(data.duBundleStartsAt) * 1000 : null;
  const end = data.duBundleEndsAt ? Number(data.duBundleEndsAt) * 1000 : null;
  const valid = data.duBundleDiscountValid === 'true' && percent > 0 && percent <= 100
    && (start === null || Number.isFinite(start) && now >= start)
    && (end === null || Number.isFinite(end) && now < end)
    && (start === null || end === null || start < end);
  const addons = new Set((data.duBundleAddonIds || '').split(',').filter(Boolean));
  const items = cartItems(cart);
  const sourceQuantity = items.filter((i) => numericId(i.product_id) === data.duBundleSource)
    .reduce((n, i) => n + Math.max(0, Number(i.quantity) || 0), 0);
  const addonQuantity = items.filter((i) => addons.has(numericId(i.product_id)))
    .reduce((n, i) => n + Math.max(0, Number(i.quantity) || 0), 0);
  const effectiveQuantity = data.duBundleContext === 'pdp' ? Math.max(sourceQuantity, 1) : sourceQuantity;
  return { valid, active: valid && (isFullPair(data) || effectiveQuantity > addonQuantity), sourceQuantity, addonQuantity };
}

export function bundleItems(cart, data, addonId, state) {
  const items = [{ id: Number(addonId), quantity: 1 }];
  if (state.active && data.duBundleContext === 'pdp'
    && (isFullPair(data) || !cartItems(cart).some((i) => numericId(i.product_id) === data.duBundleSource))) {
    items.unshift({ id: Number(data.duBundleSourceVariant), quantity: 1 });
  }
  return items;
}

export function pricePreviewActive(cart, data, state) {
  if (!isFullPair(data)) return state.active;
  if (!cart) return data.duBundlePricePreview === 'true';
  if (!Array.isArray(cart.cart_level_discount_applications) || cart.cart_level_discount_applications.length > 0) return false;
  const knownProducts = new Set([data.duBundleSource, ...(data.duBundleAddonIds || '').split(',').filter(Boolean)]);
  return state.valid && Array.isArray(cart.items) && state.sourceQuantity >= state.addonQuantity
    && cart.items.every((item) => knownProducts.has(numericId(item.product_id))
      && Array.isArray(item.line_level_discount_allocations)
      && item.line_level_discount_allocations.every((allocation) => {
        if (typeof allocation?.amount !== 'number' && typeof allocation?.amount !== 'string') return false;
        const amount = Number(allocation?.amount);
        if (!Number.isFinite(amount) || amount < 0 || String(allocation.amount).trim() === '') return false;
        if (amount === 0) return true;
        const application = allocation.discount_application;
        if (typeof application?.title !== 'string' || !application.title.startsWith('W zestawie taniej')) return false;
        return application.type === 'automatic'
          || (application.type === '' && application.value_type === 'percentage'
            && application.allocation_method === 'each' && application.target_selection === 'entitled'
            && application.target_type === 'line_item'
            && Number(application.value) === Number(data.duBundleDiscountPercent));
      }));
}

function updateOffer(aside, cart) {
  const data = aside.dataset;
  const state = bundleOfferState(cart, data);
  const active = state.valid && (isFullPair(data) || (cart ? state.active : data.duBundleDiscountActive === 'true'));
  const pricePreview = active && pricePreviewActive(cart, data, state);
  const priceUncertain = isFullPair(data) && active && !pricePreview;
  data.duBundleDiscountActive = String(active);
  data.duBundleAllDiscounted = String(active);
  data.duBundlePricePreview = String(pricePreview);
  const title = active ? data.duBundleOfferTitle : data.duBundleNeutralTitle;
  if (title) aside.setAttribute('aria-label', title);
  const scope = aside.closest('dialog') || aside;
  scope.querySelectorAll('[data-du-bundle-title]').forEach((node) => { if (title) node.textContent = title; });
  scope.querySelectorAll('[data-du-bundle-discount-copy]').forEach((node) => { node.hidden = !pricePreview; });
  scope.querySelectorAll('[data-du-bundle-offer-copy]').forEach((node) => { node.hidden = !active; });
  scope.querySelectorAll('[data-du-bundle-price-uncertain-copy], [data-du-bundle-before-discount-copy]').forEach((node) => { node.hidden = !priceUncertain; });
  scope.querySelectorAll('[data-du-bundle-regular-copy]').forEach((node) => { node.hidden = pricePreview; });
  scope.querySelectorAll('[data-du-bundle-modal-subtitle]').forEach((node) => {
    const text = active ? node.dataset.offerSubtitle : node.dataset.neutralSubtitle;
    if (text) node.textContent = text;
  });
  aside.querySelectorAll('[data-du-bundle-pair]').forEach((button) => {
    if (button.disabled) return;
    const label = active ? button.dataset.duBundlePairLabel : button.dataset.duBundleNeutralLabel;
    if (label) button.textContent = label;
  });
  return state;
}

function syncOffers(cart = lastKnownCart) {
  document.querySelectorAll('[data-du-bundle-source]').forEach((aside) => updateOffer(aside, cart));
}

function sourceError(aside) {
  return new Error(aside?.dataset.duBundleSourceError || 'Wybrany produkt lub jego konfiguracja się zmieniły. Dodaj produkt głównym przyciskiem, a następnie wybierz dodatek.');
}

function currentSource(aside, button) {
  const boundFormId = aside.dataset.duBundleFormId;
  const form = boundFormId ? document.getElementById(boundFormId)
    : aside.closest('.buy-buttons-block')?.querySelector('product-form-component form');
  const component = form?.closest('product-form-component');
  const sectionId = aside.dataset.duBundleSectionId;
  const section = sectionId ? document.getElementById('shopify-section-' + sectionId) : null;
  if (boundFormId && (!section || !section.contains(aside) || !section.contains(form)
    || component?.dataset.sectionId !== sectionId)) throw sourceError(aside);
  const variant = form?.querySelector('[name="id"]');
  const submit = form?.querySelector('[ref="addToCartButton"], button[type="submit"]');
  const qty = form?.querySelector('[name="quantity"]');
  const handoff = new URLSearchParams(window.location.search);
  const configured = ['du_flexi', 'fx_shape', 'fx_color', 'fx_width', 'fx_depth', 'fx_cut'].some((key) => handoff.has(key))
    || form?.dataset.duFlexiForm && form.dataset.duFlexiForm !== 'inactive'
    || Array.from(form?.elements || []).some((input) => !input.disabled
      && (input.name?.startsWith('properties[') || input.name === 'selling_plan') && input.value);
  // Configured products must use the native form, which owns properties and FLEXI validation.
  if (!button.isConnected || !aside.isConnected || !form || variant?.disabled || !submit || submit.disabled
    || submit.getAttribute('aria-disabled') === 'true' || configured
    || numericId(component.dataset.productId) !== aside.dataset.duBundleSource
    || numericId(variant?.value) !== aside.dataset.duBundleSourceVariant
    || Number(qty?.min || 1) > 1 || Number(qty?.step || 1) > 1) throw sourceError(aside);
  return { form, id: numericId(variant.value) };
}

async function readCart() {
  const response = await fetch(cartJsonUrl(), { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('Cart refresh failed');
  const cart = await response.json();
  if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
  lastKnownCart = cart;
  return cart;
}

async function prepareItems(id, button, activeAtClick, previewAtClick) {
  const aside = button?.closest('[data-du-bundle-source]');
  if (!button?.hasAttribute('data-du-bundle-pair') || !aside) return [{ id, quantity: 1 }];
  const fullPair = isFullPair(aside.dataset);
  await Promise.allSettled([...pendingCartUpdates]);
  const revision = cartRevision;
  const cart = await readCart();
  const state = updateOffer(aside, cart);
  if ((fullPair && !activeAtClick) || ((activeAtClick || fullPair) && !state.active)) throw new Error(aside.dataset.duBundleOfferChangedError
    || 'Oferta zestawu uległa zmianie. Sprawdź aktualne warunki przed dodaniem produktu.');
  if (fullPair && previewAtClick && aside.dataset.duBundlePricePreview !== 'true') throw new Error(aside.dataset.duBundleOfferChangedError
    || 'Oferta zestawu uległa zmianie. Sprawdź aktualne warunki przed dodaniem produktu.');
  if (!state.active) return [{ id, quantity: 1 }];
  // A neutral button never silently becomes a two-product purchase after a cart change.
  if (!activeAtClick) return [{ id, quantity: 1 }];
  const initial = currentSource(aside, button);
  const handle = aside.dataset.duBundleSourceHandle;
  if (!handle || !/^[a-z0-9-]+$/i.test(handle)) throw sourceError(aside);
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`,
    { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw sourceError(aside);
  const product = await response.json();
  const variant = product.variants?.find((v) => numericId(v.id) === initial.id);
  if (numericId(product.id) !== aside.dataset.duBundleSource || variant?.available !== true
    || Number(variant.quantity_rule?.min || 1) > 1 || Number(variant.quantity_rule?.increment || 1) > 1
    || currentSource(aside, button).form !== initial.form || cartRevision !== revision) throw sourceError(aside);
  // The native endpoint decides actual prices/stock. No discount code or price is sent.
  const finalState = updateOffer(aside, cart);
  if (!finalState.active || fullPair !== isFullPair(aside.dataset)
    || (fullPair && previewAtClick && aside.dataset.duBundlePricePreview !== 'true')) throw new Error(aside.dataset.duBundleOfferChangedError
    || 'Oferta zestawu uległa zmianie. Sprawdź aktualne warunki przed dodaniem produktu.');
  return bundleItems(cart, aside.dataset, id, finalState);
}

async function addVariant(variantId, button) {
  const id = Number(variantId);
  if (!id || addingBundle || uncertainAdd || button?.disabled) return;

  addingBundle = true;
  const activeAtClick = button?.closest('[data-du-bundle-source]')?.dataset.duBundleDiscountActive === 'true';
  const previewAtClick = button?.closest('[data-du-bundle-source]')?.dataset.duBundlePricePreview === 'true';
  const original = button?.textContent;
  if (button) {
    button.disabled = true;
    button.textContent = button.dataset.addingLabel || 'Dodaję…';
  }

  let deferred;
  let didAdd = false;
  let cartSynced = false;
  let requestStarted = false;
  let confirmedFailure = false;

  try {
    const items = await prepareItems(id, button, activeAtClick, previewAtClick);
    deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'add', context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: deferred.promise });
    event.duBundleAdd = true;
    document.dispatchEvent(event);
    const payload = {
      items,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    };
    requestStarted = true;
    const response = await fetch(cartAddUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      // Shopify documents partial quantity adds with 422; a pair is not an atomic transaction.
      confirmedFailure = response.status === 400;
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Cart request failed');
    }
    // A 2xx response means the server accepted the add. Preserve that fact
    // even if reading its JSON or refreshing the cart fails afterward.
    didAdd = true;
    const data = await response.json();
    if (data.status) {
      throw new Error(data.description || data.message || 'Cart response failed');
    }

    const ajaxCart = await readCart();
    cartSynced = true;
    syncOffers(ajaxCart);
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        itemCount: ajaxCart.item_count,
        sections: data.sections,
        source: 'durasan-bundle',
        didError: false,
      },
    });

    if (button) button.textContent = button.dataset.addedLabel || 'Dodano';
    closeModal();
    openCartDrawer();
  } catch (error) {
    deferred?.reject(error);
    uncertainAdd = requestStarted && !didAdd && !confirmedFailure;
    if (didAdd || uncertainAdd) {
      // The add request succeeded. A failed cart refresh must not invite a
      // second click, which would add the same accessory twice.
      if (button) button.textContent = didAdd ? button.dataset.addedLabel || 'Dodano' : original;
      if (uncertainAdd) document.dispatchEvent(new CartErrorEvent({
        error: `${error?.message || ''} ${button?.closest('[data-du-bundle-source]')?.dataset.duBundleUncertainError
          || 'Nie udało się potwierdzić dodania. Sprawdź koszyk przed ponowną próbą.'}`.trim(), code: 'UNKNOWN' }));
      closeModal();
      holdDrawer(false);
      window.location.assign(globalThis.Theme?.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
    } else {
      document.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );
      if (button) button.textContent = original || 'Dodaj';
    }
  } finally {
    addingBundle = false;
    if ((!didAdd || cartSynced) && !uncertainAdd) {
      window.setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        if (original) button.textContent = original;
        syncOffers();
      }, 1600);
    }
  }
}

function modalEl() {
  return document.getElementById('du-bundle-modal');
}

function holdDrawer(on) {
  if (on) document.documentElement.setAttribute('data-du-bundle-hold-drawer', '');
  else document.documentElement.removeAttribute('data-du-bundle-hold-drawer');
}

function openCartDrawer() {
  holdDrawer(false);
  const drawer = document.querySelector('#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }
  document.querySelector('cart-icon, header-actions [href="/cart"]')?.click();
}

function closeCompetingDialogs(keep) {
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    if (dialog === keep) return;
    try {
      dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
  });
}

function dialogIsOpen(dialog) {
  return Boolean(
    dialog?.open ||
      dialog?.hasAttribute('open') ||
      dialog?.classList.contains('is-du-open')
  );
}

function forceOpenStyles(dialog) {
  dialog.style.setProperty('display', 'grid', 'important');
  dialog.style.setProperty('position', 'fixed', 'important');
  dialog.style.setProperty('inset', '0', 'important');
  dialog.style.setProperty('z-index', '2147483000', 'important');
  dialog.style.setProperty('width', '100vw', 'important');
  dialog.style.setProperty('min-width', '100vw', 'important');
  dialog.style.setProperty('height', '100dvh', 'important');
  dialog.style.setProperty('max-width', '100vw', 'important');
  dialog.style.setProperty('max-height', '100dvh', 'important');
}

function applyOpen(dialog) {
  dialog.setAttribute('open', '');
  dialog.classList.add('is-du-open');
  document.documentElement.classList.add('du-bundle-modal-open');
  forceOpenStyles(dialog);
}

function applyClosed(dialog) {
  dialog.classList.remove('is-du-open');
  document.documentElement.classList.remove('du-bundle-modal-open');
  dialog.style.removeProperty('display');
  dialog.style.removeProperty('width');
  dialog.style.removeProperty('min-width');
  dialog.style.removeProperty('height');
  dialog.style.removeProperty('max-width');
  dialog.style.removeProperty('max-height');
}

function tryShowDialog(dialog) {
  if (!dialog) return false;
  openingModal = true;
  closeCompetingDialogs(dialog);

  try {
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    else if (!dialog.open) dialog.setAttribute('open', '');
  } catch {
    try {
      dialog.close?.();
    } catch {
      /* already closed */
    }
    closeCompetingDialogs(dialog);
    try {
      dialog.showModal?.();
    } catch {
      try {
        dialog.show?.();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
  }

  applyOpen(dialog);
  openingModal = false;
  const box = dialog.getBoundingClientRect();
  return dialogIsOpen(dialog) || dialog.classList.contains('is-du-open') || box.width > 50;
}

function closeModal() {
  const dialog = modalEl();
  if (!dialog) {
    holdDrawer(false);
    return;
  }
  applyClosed(dialog);
  if (dialog.open) {
    try {
      dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }
  } else {
    dialog.removeAttribute('open');
  }
  holdDrawer(false);
}

function waitFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

async function openModal() {
  const dialog = modalEl();
  if (!dialog || seen()) return false;
  holdDrawer(true);

  for (let i = 0; i < 10; i += 1) {
    if (tryShowDialog(dialog)) {
      markSeen();
      dialog.querySelector('.du-bundle__add--primary, [data-du-bundle-add]')?.focus?.();
      return true;
    }
    await waitFrame();
  }

  holdDrawer(false);
  return false;
}

async function maybeOpenFromCart(detail) {
  if (addingBundle || seen()) return false;
  const cfg = config();
  const dialog = modalEl();
  if (!cfg.sourceProductId || !dialog) return false;
  lastKnownCart = detail;
  dialog.querySelectorAll('[data-du-bundle-source]').forEach((aside) => updateOffer(aside, detail));
  const items = cartItems(detail);
  const sourceId = numericId(cfg.sourceProductId);
  if (!items.some((item) => numericId(item.product_id) === sourceId)) return false;
  const offers = [...dialog.querySelectorAll('[data-du-bundle-offer-product]')];
  if (!offers.length) return false;
  // Avoid re-offering an accessory already purchased, even when other offers remain.
  const cartProducts = new Set(items.map((item) => numericId(item.product_id)));
  if (offers.some((offer) => cartProducts.has(numericId(offer.dataset.duBundleOfferProduct)))) return false;
  return openModal();
}

function onCartUpdate(event) {
  if (event.action !== 'add' || addingBundle || seen() || !modalEl() || !isSourceAddition(event, config())) return;
  holdDrawer(true);
  // Only an addition of this popup's exact source may delay the normal cart drawer.
  // Checking the full cart also prevents recommending an accessory already present.
  event.duBundleDecision = Promise.resolve(event.promise)
    .then(async (result) => {
      if (result?.detail?.didError || addingBundle || seen()) return false;
      const response = await fetch(cartJsonUrl());
      if (!response.ok) return false;
      return maybeOpenFromCart(await response.json());
    })
    .catch(() => false)
    .then((opened) => {
      if (!opened) holdDrawer(false);
      return opened;
    });
}

function syncPdpBundles() {
  // These lists are explicitly configured by the merchant and remain available
  // alongside size/colour pickers, including after a sibling-product navigation.
  document.querySelectorAll('.product-information .du-bundle--pdp').forEach((bundle) => {
    if (!(bundle instanceof HTMLElement)) return;
    bundle.hidden = false;
    bundle.setAttribute('data-du-force-show', '');
  });
}

/* --- Karuzela „W zestawie taniej” --------------------------------------
   Delegacja zamiast wiązania na elementach: koszyk i szuflada przerysowują
   swój HTML po każdej zmianie, więc każdy uchwyt zapięty na konkretnym
   przycisku i tak by przepadł. */

function bundleSlides(track) {
  return Array.from(track.querySelectorAll('[data-du-bundle-slide]'));
}

function bundleStep(track, slides) {
  const step = slides.length > 1 ? slides[1].offsetLeft - slides[0].offsetLeft : 0;
  return step > 0 ? step : track.clientWidth || 1;
}

function syncBundleCarousel(track) {
  const root = track.closest('[data-du-bundle]');
  if (!root) return;
  const slides = bundleSlides(track);
  if (slides.length < 2) return;
  const index = Math.round(track.scrollLeft / bundleStep(track, slides));
  root.querySelectorAll('.du-bundle__dot').forEach((dot, i) => {
    dot.classList.toggle('is-active', i === index);
  });
  root.querySelectorAll('[data-du-bundle-prev]').forEach((b) => {
    b.disabled = index <= 0;
  });
  root.querySelectorAll('[data-du-bundle-next]').forEach((b) => {
    b.disabled = index >= slides.length - 1;
  });
}

function syncBundleCarousels() {
  document.querySelectorAll('[data-du-bundle-track]').forEach(syncBundleCarousel);
}

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof HTMLElement) || !track.matches?.('[data-du-bundle-track]')) return;
    syncBundleCarousel(track);
  },
  { capture: true, passive: true }
);

document.addEventListener('click', (event) => {
  const arrow = event.target.closest?.('[data-du-bundle-prev], [data-du-bundle-next]');
  if (arrow) {
    event.preventDefault();
    event.stopPropagation();
    const track = arrow.closest('[data-du-bundle]')?.querySelector('[data-du-bundle-track]');
    if (!track) return;
    const slides = bundleSlides(track);
    const step = bundleStep(track, slides);
    const dir = arrow.hasAttribute('data-du-bundle-next') ? 1 : -1;
    const target = Math.min(
      slides.length - 1,
      Math.max(0, Math.round(track.scrollLeft / step) + dir)
    );
    track.scrollTo({ left: target * step, behavior: 'smooth' });
    return;
  }

  const add = event.target.closest?.('[data-du-bundle-add]');
  if (add) {
    event.preventDefault();
    event.stopPropagation();
    addVariant(add.dataset.variantId, add);
    return;
  }
  if (event.target.closest?.('[data-du-bundle-dismiss]')) {
    event.preventDefault();
    markSeen();
    closeModal();
    openCartDrawer();
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, onCartUpdate, { capture: true });
document.addEventListener('durasan:sibling:changed', () => {
  window.requestAnimationFrame(syncBundles);
});

const dialog = modalEl();
dialog?.addEventListener('cancel', (event) => {
  event.preventDefault();
  markSeen();
  closeModal();
  window.setTimeout(openCartDrawer, 80);
});
dialog?.addEventListener('close', () => {
  if (openingModal) return;
  applyClosed(dialog);
  if (!addingBundle) holdDrawer(false);
});
dialog?.addEventListener('click', (event) => {
  if (event.target === dialog) {
    markSeen();
    closeModal();
    openCartDrawer();
  }
});

function syncBundles() {
  syncPdpBundles();
  syncBundleCarousels();
  syncOffers();
  scheduleOfferExpiry();
}

let expiryTimer;
function scheduleOfferExpiry() {
  window.clearTimeout(expiryTimer);
  const ends = [...document.querySelectorAll('[data-du-bundle-ends-at]')]
    .map((node) => Number(node.dataset.duBundleEndsAt) * 1000).filter((end) => end > Date.now());
  if (ends.length) expiryTimer = window.setTimeout(() => {
    syncOffers();
    scheduleOfferExpiry();
  }, Math.min(Math.min(...ends) - Date.now() + 10, 2147483647));
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.duBundleAdd) return;
  cartRevision += 1;
  const pending = Promise.resolve(event.promise).then((result) => {
    if (Array.isArray(result?.detail?.items)) {
      lastKnownCart = result.detail;
      syncOffers();
    }
  }).catch(() => {}).finally(() => pendingCartUpdates.delete(pending));
  pendingCartUpdates.add(pending);
}, { capture: true });

syncBundles();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncBundles, { once: true });
}
window.addEventListener('pageshow', syncBundles);
window.addEventListener('resize', syncBundleCarousels);
document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  window.setTimeout(syncBundleCarousels, 120);
  window.setTimeout(syncBundleCarousels, 600);
});
window.setTimeout(syncBundles, 50);
window.setTimeout(syncBundles, 400);
